import React, { useRef } from 'react';
import { parseISO, format as dateFnsFormat, differenceInDays } from 'date-fns';
import { es, ptBR, enUS, de } from 'date-fns/locale'
import distanceInWord from 'date-fns/formatDistanceToNow';
import i18n from 'i18n';
import accents from 'remove-accents';

const locales = { es, pt: ptBR, en: enUS, de }
const localeWithCountry = { es: 'es-MX', pt: 'pt-BR', en: 'en-US', de: 'de-DE' }

export const useCancellablePromises = () => {
  const pendingPromises = useRef([]);

  const appendPendingPromise = promise => pendingPromises.current = [...pendingPromises.current, promise];

  const removePendingPromise = promise => pendingPromises.current = pendingPromises.current.filter(p => p !== promise);

  const clearPendingPromises = () => pendingPromises.current.map(p => p.cancel());

  const api = {
    appendPendingPromise,
    removePendingPromise,
    clearPendingPromises
  };
  return api;
};
export const cancellablePromise = promise => {
  let isCanceled = false;
  const wrappedPromise = new Promise((resolve, reject) => {
    promise.then(value => isCanceled ? reject({
      isCanceled,
      value
    }) : resolve(value), error => reject({
      isCanceled,
      error
    }));
  });
  return {
    promise: wrappedPromise,
    cancel: () => isCanceled = true
  };
};
export const noop = () => { };
export const delay = n => new Promise(resolve => setTimeout(resolve, n));
export function convertToBase64(file, readAsBuffer = false) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    if (!file) return reject(); // Convert the file to base64 text

    readAsBuffer ? reader.readAsArrayBuffer(file) : reader.readAsDataURL(file); // on reader load somthing...

    reader.onload = () => {
      const fileInfo = {
        name: file.name,
        type: file.type,
        size: Math.round(file.size / 1000) + ' kB',
        base64: reader.result,
        file: file
      };
      resolve(fileInfo);
    };
  });
}

export async function digestMessageHex(message) {
  const hashBuffer = await crypto.subtle.digest('SHA-256', message);           // hash the message
  const hashArray = Array.from(new Uint8Array(hashBuffer));                     // convert buffer to byte array
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join(''); // convert bytes to hex string
  return hashHex;
}

export const parseDate = (date, formatStr = 'PPpp') => {
  if (!date) return 'N/A';
  return dateFnsFormat(parseISO(date), formatStr, {
    locale: locales[i18n.language.toLowerCase() ?? 'es']
  });
};

export const formatDistanceToNow = date => {
  if (!date) return '';
  return distanceInWord(new Date(date), {
    addSuffix: true,
    locale: locales[i18n.language.toLowerCase() ?? 'es']
  });
};

export const format = (date, formatStr = 'PPpp', { locale, ...options } = {}) => {
  if (!date) return '';
  return dateFnsFormat(new Date(date), formatStr, {
    locale: locales[locale ?? i18n.language.toLowerCase() ?? 'es'],
    ...options
  });
};
export function getApiName(str, index) {
  return `p${index}_${camelize(str)}`
}


export const getErrorList = (error = '') => {
  if (Array.isArray(error)) return error[0].error; else return error;
};
export const getDateDiffInDays = (startDate, endDate) => {
  let date = new Date(endDate);
  if (!endDate) date = new Date();
  const days = differenceInDays(date, new Date(startDate));
  return days;
};
export const truncate = (text = '', limit) => {
  if (text.length < limit) return text;
  return `${text.slice(0, limit).trim()}...`;
};
export const downloadFile = ({
  name,
  url,
  suffix,
  ext = '',
  blob
}) => async () => {
  try {
    const windowsURL = window.URL || window.webkiURL;
    let newUrl = null;
    if (url) {
      const response = await fetch(url);
      const blobObject = await response.blob();
      newUrl = windowsURL.createObjectURL(new Blob([blobObject]));
    } else {
      newUrl = windowsURL.createObjectURL(blob);
    }
    const link = document.createElement('a');
    link.href = newUrl;
    link.setAttribute('download', `${name}${suffix ? `-${suffix}` : ''}.${ext}`);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
  } catch (error) {
    console.log({
      error
    });
  }
};
export function getMongoObjectId() {
  const timestamp = (new Date().getTime() / 1000 | 0).toString(16);
  return timestamp + 'xxxxxxxxxxxxxxxx'.replace(/[x]/g, function () {
    return (Math.random() * 16 | 0).toString(16);
  }).toLowerCase();
}
;
export function getPrice(price, currency, withCurrency) {
  switch (currency) {
    case 'mxn':
    case 'usd':
      return `$${(price / 100).toFixed(2)} ${withCurrency ? currency?.toUpperCase() : ''}`;

    default:
      return `$${(price / 100).toFixed(2)} ${currency?.toUpperCase()}`;
  }
}

function getLanguage() {
  const [userLanguage] = i18n.language.split('-');

  return userLanguage;
}

export function formatCurrency(value) {
  const language = getLanguage();
  const locale = localeWithCountry[language] ?? 'es-MX';

  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).format(value)
}


export function getFileExtension(filename = '') {
  return filename.trim().split('.').pop().toLowerCase();
}

export function isValidFileType({ filename = '', extension = '', acceptedExtensions = [] }) {
  return acceptedExtensions.some((acceptedExt) =>
    (extension && acceptedExt.includes(extension)) ||
    (filename && getFileExtension(filename).endsWith(acceptedExt)));
}

export function findSelectOption(options = [], value = '') { return options.find((o) => o && o.value === value) };
export function createSelectOptions(options = '') {
  if (Array.isArray(options)) return options.map(o => ({ value: o, label: o }))
  else return ({ value: options, label: options })
}
export const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/
function checkTratoDomain(domain) {
  return ['freemium', 'starter', 'enterprise', 'pyme', 'pro', 'enterprise-test', 'enterprise-stage'].includes(domain);
}
export function redirectTo(domain = '', jwt = '', username = '') {
  const isTratoDomain = checkTratoDomain()
  const isInCorrectDomain = window.location.href.includes(isTratoDomain ? 'starter' : domain);
  const isDev = process.env.NODE_ENV === 'development' || window.location.href.includes('starter-test');
  // const isDev = false;
  if (!isInCorrectDomain && !isDev) {
    localStorage.removeItem('auth');
  }
  if (!isTratoDomain && !isInCorrectDomain && !isDev) {
    window.location.href = `https://${domain}.trato.io/auth/redirect?token=${jwt}&username=${username}`
  } else if (isTratoDomain && !isInCorrectDomain && !isDev) {
    // window.location.href = `https://starter.trato.io/auth/redirect?token=${jwt}&username=${username}`
    window.location.href = `https://starter.trato.io/auth/redirect?token=${jwt}&username=${username}`
  }
}

export function camelize(str) {
  return accents(str)
    .replace(/[^\w\s]/gi, '')
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (letter, index) => {
      return index === 0 ? letter.toLowerCase() : letter.toUpperCase();
    })
    .replace(/\s+/g, '');
}

export const isDate = (date) => {
  return (new Date(date) !== "Invalid Date") && !isNaN(new Date(date));
}

export function range(start, end) {
  return Array.from(Array(end + 1).keys()).slice(start);
}

export function zeroPad(input, length) {
  return (Array(length + 1).join('0') + input).slice(-length);
}

export function initialLetters(name = '', lastName = '') {
  if (!name) return '';
  const fullName = `${name}${lastName ? ` ${lastName}` : ''}`
  let result = '';
  const splited = fullName.split(' ');
  for (let i = 0; i < (splited.length > 2 ? 2 : splited.length); i++) {
    const element = splited[i];
    result += element.substring(0, 1);
  }
  return result.toUpperCase();
};

export function isMobile() {
  var check = false;
  const urlParams = new URLSearchParams(window.location.search);
  const isMobile = urlParams.get('isMobile');
  if (isMobile) return isMobile === 'true';
  (function (a) { if (/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(a) || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0, 4))) check = true; })(navigator.userAgent || navigator.vendor || window.opera);
  return check;
}

export function arrayToObject(array = [], key, value, keyValue) {
  return array.reduce((obj, item) => ({
    ...obj,
    [item[key]]: keyValue ? item[keyValue] : value
  }), {})
}

export function extractHTMLContent(s) {
  const div = document.createElement('div');
  div.innerHTML = s;
  return div.textContent || div.innerText;
};

export function getIsVariableFilled({isLimitRequired, limit = 10000, value = ''}) {
  return isLimitRequired ? value.length === limit : !!value;
}

export function getKey(obj, key) {
  const keys = key.split('.'); 
  if (keys.length < 2) {
    return obj[key]
  }
  
  return keys.reduce((object, key) => {
    if (!object[key]) return '';
    return object[key]
  }, obj)
}

export function mapDataToReactSelectOptions(arr = [],  options = {}) {
  return  arr.map(item => getReactSelectOption(item, options))
}

export function getReactSelectOption(item = [],  options = {}) {
  const { labelKey = 'name', valueKey = '_id' } = options;

  return { label: item[labelKey], value: item[valueKey], ...item }
}